import { useGetList, useGetOne, useTranslate } from "ra-core";
import {
  Building,
  Contact as ContactIcon,
  Handshake,
  Mail,
  StickyNote,
  Target,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";

import { formatRelativeDate } from "../misc/RelativeDate";
import type {
  Company,
  CompanyNote,
  Contact,
  Deal,
  EmailMessage,
  EmailRecipient,
  Sale,
} from "../types";

// «Kjenner vi dem?»-panelet (PR-S6 i søkeplanen): når et globaltreff er en
// kunde eller et prospekt, viser paletten selgerens sjekkliste før kontakt
// i ett blikk — kundestatus, kontakter, åpne avtaler, siste notat og siste
// utsending. I dag fire oppslag, her ett.
export type KjennerViDemTarget =
  | { type: "company"; id: string }
  | { type: "prospect"; orgNr: string; navn: string };

const nokFormat = new Intl.NumberFormat("nb-NO", {
  style: "currency",
  currency: "NOK",
  maximumFractionDigits: 0,
});

const stripHtml = (value: string) =>
  value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const Row = ({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-1">
    <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
      <Icon className="h-3.5 w-3.5" />
      {label}
    </div>
    <div className="text-sm">{children}</div>
  </div>
);

const Empty = ({ children }: { children: React.ReactNode }) => (
  <span className="text-muted-foreground">{children}</span>
);

// Sjekklisten for en eksisterende kunde.
const CompanyContext = ({ companyId }: { companyId: string }) => {
  const translate = useTranslate();

  const { data: company } = useGetOne<Company>("companies", { id: companyId });
  const { data: manager } = useGetOne<Sale>(
    "sales",
    { id: company?.sales_id ?? 0 },
    { enabled: company?.sales_id != null },
  );
  const { data: contacts = [] } = useGetList<Contact>("contacts", {
    filter: { company_id: companyId },
    sort: { field: "last_seen", order: "DESC" },
    pagination: { page: 1, perPage: 4 },
  });
  const { data: deals = [] } = useGetList<Deal>("deals", {
    filter: { company_id: companyId, "archived_at@is": "null" },
    sort: { field: "amount", order: "DESC" },
    pagination: { page: 1, perPage: 3 },
  });
  const { data: notes = [] } = useGetList<CompanyNote>("company_notes", {
    filter: { company_id: companyId },
    sort: { field: "created_at", order: "DESC" },
    pagination: { page: 1, perPage: 1 },
  });
  const { data: recipientRows = [] } = useGetList<EmailRecipient>(
    "email_recipients",
    {
      filter: { company_id: companyId },
      sort: { field: "status_at", order: "DESC" },
      pagination: { page: 1, perPage: 1 },
    },
  );
  const { data: lastEmail } = useGetOne<EmailMessage>(
    "email_messages",
    { id: recipientRows[0]?.email_message_id ?? 0 },
    { enabled: recipientRows.length > 0 },
  );
  const lastNote = notes[0];

  if (!company) return null;

  return (
    <div className="space-y-4">
      <div>
        <div className="font-medium">{company.name}</div>
        <div className="text-xs text-muted-foreground">
          {[
            company.city,
            company.tax_identifier && `Org.nr ${company.tax_identifier}`,
            company.customer_number &&
              `${translate("crm.kjenner.customer_number", { _: "Kundenr." })} ${company.customer_number}`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
        {manager && (
          <div className="mt-1 text-xs text-muted-foreground">
            {translate("crm.kjenner.manager", { _: "Kundeansvarlig" })}:{" "}
            {manager.first_name} {manager.last_name}
          </div>
        )}
      </div>

      <Row
        icon={ContactIcon}
        label={translate("resources.contacts.name", { smart_count: 2 })}
      >
        {contacts.length === 0 ? (
          <Empty>
            {translate("crm.kjenner.no_contacts", { _: "Ingen kontakter" })}
          </Empty>
        ) : (
          <ul className="space-y-0.5">
            {contacts.map((c) => (
              <li key={c.id} className="truncate">
                {c.first_name} {c.last_name}
                {c.title && (
                  <span className="text-muted-foreground"> · {c.title}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Row>

      <Row
        icon={Handshake}
        label={translate("crm.kjenner.open_deals", { _: "Åpne muligheter" })}
      >
        {deals.length === 0 ? (
          <Empty>
            {translate("crm.kjenner.no_deals", { _: "Ingen åpne muligheter" })}
          </Empty>
        ) : (
          <ul className="space-y-0.5">
            {deals.map((d) => (
              <li key={d.id} className="truncate">
                {d.name}
                {d.amount ? (
                  <span className="text-muted-foreground">
                    {" "}
                    · {nokFormat.format(d.amount)}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Row>

      <Row
        icon={StickyNote}
        label={translate("crm.kjenner.last_note", { _: "Siste notat" })}
      >
        {lastNote ? (
          <div>
            <span className="line-clamp-2">
              {stripHtml(lastNote.content ?? "")}
            </span>
            <span className="text-xs text-muted-foreground">
              {formatRelativeDate(lastNote.created_at)}
            </span>
          </div>
        ) : (
          <Empty>
            {translate("crm.kjenner.no_notes", { _: "Ingen notater" })}
          </Empty>
        )}
      </Row>

      <Row
        icon={Mail}
        label={translate("crm.kjenner.last_email", { _: "Siste utsending" })}
      >
        {lastEmail ? (
          <div>
            <span className="line-clamp-1">{lastEmail.subject}</span>
            <span className="text-xs text-muted-foreground">
              {formatRelativeDate(lastEmail.created_at)}
            </span>
          </div>
        ) : (
          <Empty>
            {translate("crm.kjenner.no_emails", {
              _: "Ingen utsendinger mottatt",
            })}
          </Empty>
        )}
      </Row>
    </div>
  );
};

// Prospekt: finnes firmaet allerede i kundelisten (org.nr-kobling)?
const ProspectContext = ({ orgNr, navn }: { orgNr: string; navn: string }) => {
  const translate = useTranslate();
  const { data: matches = [], isPending } = useGetList<Company>("companies", {
    filter: { tax_identifier: orgNr },
    sort: { field: "id", order: "ASC" },
    pagination: { page: 1, perPage: 1 },
  });

  if (isPending) return null;

  const match = matches[0];
  if (match) {
    return (
      <div className="space-y-4">
        <Badge variant="secondary">
          {translate("crm.kjenner.already_customer", {
            _: "Allerede i kundelisten",
          })}
        </Badge>
        <CompanyContext companyId={String(match.id)} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <div className="font-medium">{navn}</div>
        <div className="text-xs text-muted-foreground">Org.nr {orgNr}</div>
      </div>
      <Badge variant="outline">
        {translate("crm.kjenner.not_customer", { _: "Ikke i kundelisten" })}
      </Badge>
      <p className="text-xs text-muted-foreground">
        {translate("crm.kjenner.prospect_hint", {
          _: "Ingen kunde med dette org.nr. Åpne treffet for å se prospektet.",
        })}
      </p>
    </div>
  );
};

export const KjennerViDemPanel = ({
  target,
}: {
  target: KjennerViDemTarget | null;
}) => {
  const translate = useTranslate();

  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {target?.type === "prospect" ? (
          <Target className="h-3.5 w-3.5" />
        ) : (
          <Building className="h-3.5 w-3.5" />
        )}
        {translate("crm.kjenner.title", { _: "Kjenner vi dem?" })}
      </div>
      {target == null ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.kjenner.hint", {
            _: "Marker en kunde eller et prospekt i trefflisten for å se kontekst.",
          })}
        </p>
      ) : target.type === "company" ? (
        <CompanyContext companyId={target.id} />
      ) : (
        <ProspectContext orgNr={target.orgNr} navn={target.navn} />
      )}
    </div>
  );
};
